import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import {
    getLocalDateTimeInTimeZone,
    localDateTimeToUtc,
} from 'apps/marsinet/src/app/utils/timezone-utils';

import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import {
    AvailableMaterialDto,
    MaterialTableDto,
} from 'apps/marsinet/src/app/typescript-axios';

/** The DTO has no id, so we key each line by its position in the response. */
type StockLine = AvailableMaterialDto & { key: string };

type Option = { label: string; value: string };

interface AssignMaterialModalProps {
    visible: boolean;
    maintenanceOrderId: string;
    onHide: () => void;
    onSuccess?: (material: MaterialTableDto) => void;
}

const currency = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
});

/** Distinct id/name pairs, sorted by name, skipping lines with no id. */
const toOptions = (
    lines: StockLine[],
    getId: (line: StockLine) => string | undefined,
    getName: (line: StockLine) => string | undefined,
): Option[] => {
    const byId = new Map<string, string>();

    lines.forEach((line) => {
        const id = getId(line);
        if (!id || byId.has(id)) return;
        byId.set(id, getName(line) ?? id);
    });

    return [...byId.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
};

export function AssignMaterialModal({
    visible,
    maintenanceOrderId,
    onHide,
    onSuccess,
}: AssignMaterialModalProps) {
    const { t } = useTranslation();
    const { selectedTimezone } = useContext(MarsinetContext);

    const [stock, setStock] = useState<StockLine[]>([]);
    const [loadingStock, setLoadingStock] = useState(false);

    const [siteId, setSiteId] = useState<string>('');
    const [warehouseId, setWarehouseId] = useState<string>('');
    const [locationId, setLocationId] = useState<string>('');
    const [materialKey, setMaterialKey] = useState<string>('');

    const [quantity, setQuantity] = useState<number>(1);
    // El selector muestra la hora de la zona elegida y la convierte a UTC al enviar.
    const [date, setDate] = useState<Date | null>(() =>
        getLocalDateTimeInTimeZone(new Date(), selectedTimezone)
    );
    const [comments, setComments] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!visible) return;

        const loadAvailableMaterials = async () => {
            try {
                setLoadingStock(true);
                setError(null);

                const response =
                    await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetAvailableMaterials();

                setStock(
                    (response.data ?? []).map((line, index) => ({
                        ...line,
                        key: String(index),
                    })),
                );
            } catch (err) {
                console.error('[AssignMaterialModal] loading available materials', err);
                setError(t('pages.maintenance_orders.can_not_load_available_materials'));
                setStock([]);
            } finally {
                setLoadingStock(false);
            }
        };

        loadAvailableMaterials();
    }, [visible, t]);

    /* ------------------------------------------------- cascading selections */

    const siteOptions = useMemo(
        () => toOptions(stock, (l) => l.site_id, (l) => l.site_name),
        [stock],
    );

    const warehouseLines = useMemo(
        () => stock.filter((line) => line.site_id === siteId),
        [stock, siteId],
    );

    const warehouseOptions = useMemo(
        () =>
            toOptions(warehouseLines, (l) => l.warehouse_id, (l) => l.warehouse_name),
        [warehouseLines],
    );

    const locationLines = useMemo(
        () => warehouseLines.filter((line) => line.warehouse_id === warehouseId),
        [warehouseLines, warehouseId],
    );

    const locationOptions = useMemo(
        () =>
            toOptions(
                locationLines,
                (l) => l.warehouse_location_id,
                (l) => l.warehouse_location_name,
            ),
        [locationLines],
    );

    const materialLines = useMemo(
        () =>
            locationLines.filter((line) => line.warehouse_location_id === locationId),
        [locationLines, locationId],
    );

    const materialOptions = useMemo(
        () =>
            materialLines
                .map((line) => ({
                    value: line.key,
                    label: [line.article_name, line.series].filter(Boolean).join(' · '),
                    available: line.quantity,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [materialLines],
    );

    const selectedMaterial = useMemo(
        () => materialLines.find((line) => line.key === materialKey) ?? null,
        [materialLines, materialKey],
    );

    /* Reset everything below a level when it changes, and skip levels that
       only have one option — no point making someone pick from a list of one. */
    useEffect(() => {
        if (siteOptions.length === 1 && !siteId) setSiteId(siteOptions[0].value);
    }, [siteOptions, siteId]);

    useEffect(() => {
        setWarehouseId(warehouseOptions.length === 1 ? warehouseOptions[0].value : '');
    }, [warehouseOptions]);

    useEffect(() => {
        setLocationId(locationOptions.length === 1 ? locationOptions[0].value : '');
    }, [locationOptions]);

    useEffect(() => {
        setMaterialKey('');
        setQuantity(1);
    }, [locationId]);

    /* ------------------------------------------------------------- derived */

    const maxQuantity = selectedMaterial?.quantity ?? 0;
    const unitCost = selectedMaterial?.unit_cost ?? 0;
    const totalCost = unitCost * (quantity || 0);

    const isValid =
        !!selectedMaterial && !!date && quantity > 0 && quantity <= maxQuantity;

    const resetForm = () => {
        setSiteId('');
        setWarehouseId('');
        setLocationId('');
        setMaterialKey('');
        setQuantity(1);
        setDate(getLocalDateTimeInTimeZone(new Date(), selectedTimezone));
        setComments('');
        setError(null);
    };

    const handleHide = () => {
        if (submitting) return;
        resetForm();
        onHide();
    };

    const handleSubmit = async () => {
        if (!isValid || !selectedMaterial || !date) return;

        setSubmitting(true);
        setError(null);

        try {
            const response =
                await apiServices.maintenanceOrderApi.maintenanceOrdersControllerAssignMaterialToMaintenanceOrder(
                    {
                        article_id: selectedMaterial.article_id as string,
                        warehouse_id: selectedMaterial.warehouse_id as string,
                        warehouse_location_id:
                            selectedMaterial.warehouse_location_id as string,
                        site_id: selectedMaterial.site_id,
                        series: selectedMaterial.series ?? '',
                        quantity,
                        unit_cost: unitCost,
                        date: new Date(localDateTimeToUtc(date, selectedTimezone)),
                        comments: comments.trim() || undefined,
                    },
                    maintenanceOrderId,
                );

            resetForm();
            onSuccess?.(response.data);
            onHide();
        } catch (err) {
            console.error('[AssignMaterialModal] assigning material', err);
            setError(
                (err as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message ?? t('pages.maintenance_orders.assign_material_error'),
            );
        } finally {
            setSubmitting(false);
        }
    };

    const materialOptionTemplate = (option: {
        label: string;
        available: number;
    }) => (
        <div className="flex justify-content-between align-items-center gap-3">
            <span>{option.label}</span>
            <span className="text-600 text-sm white-space-nowrap">
                {option.available} {t('pages.maintenance_orders.available')}
            </span>
        </div>
    );

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label={t('cancel')}
                icon="pi pi-times"
                severity="secondary"
                text
                onClick={handleHide}
                disabled={submitting}
            />
            <Button
                label={t('save')}
                icon="pi pi-check"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!isValid || submitting}
            />
        </div>
    );

    return (
        <Dialog
            header={t('pages.maintenance_orders.assign_material')}
            visible={visible}
            onHide={handleHide}
            footer={footer}
            style={{ width: '32rem' }}
            breakpoints={{ '960px': '90vw' }}
            modal
            dismissableMask={!submitting}
        >
            <div className="flex flex-column gap-3 pt-2">
                <div className="flex flex-column gap-2">
                    <label htmlFor="site" className="font-medium text-900">
                        {t('pages.maintenance_orders.site')}
                    </label>
                    <Dropdown
                        id="site"
                        value={siteId}
                        options={siteOptions}
                        onChange={(e) => setSiteId(e.value)}
                        placeholder={t('pages.maintenance_orders.select_site')}
                        emptyMessage={t('pages.maintenance_orders.no_available_materials')}
                        loading={loadingStock}
                        filter
                        filterBy="label"
                        filterMatchMode="contains"
                    />
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="warehouse" className="font-medium text-900">
                        {t('pages.maintenance_orders.warehouse')}
                    </label>
                    <Dropdown
                        id="warehouse"
                        value={warehouseId}
                        options={warehouseOptions}
                        onChange={(e) => setWarehouseId(e.value)}
                        placeholder={t('pages.maintenance_orders.select_warehouse')}
                        disabled={!siteId}
                        filter
                        filterBy="label"
                        filterMatchMode="contains"
                    />
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="location" className="font-medium text-900">
                        {t('pages.maintenance_orders.warehouse_location')}
                    </label>
                    <Dropdown
                        id="location"
                        value={locationId}
                        options={locationOptions}
                        onChange={(e) => setLocationId(e.value)}
                        placeholder={t('pages.maintenance_orders.select_warehouse_location')}
                        disabled={!warehouseId}
                        filter
                        filterBy="label"
                        filterMatchMode="contains"
                    />
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="material" className="font-medium text-900">
                        {t('pages.maintenance_orders.material')}
                    </label>
                    <Dropdown
                        id="material"
                        value={materialKey}
                        options={materialOptions}
                        onChange={(e) => {
                            setMaterialKey(e.value);
                            setQuantity(1);
                        }}
                        placeholder={t('pages.maintenance_orders.select_material')}
                        itemTemplate={materialOptionTemplate}
                        disabled={!locationId}
                        filter
                        filterBy="label"
                        filterMatchMode="contains"
                    />
                </div>

                <div className="flex gap-3">
                    <div className="flex flex-column gap-2 flex-1">
                        <label htmlFor="quantity" className="font-medium text-900">
                            {t('pages.maintenance_orders.quantity')}
                        </label>
                        <InputNumber
                            inputId="quantity"
                            value={quantity}
                            onValueChange={(e) => setQuantity(e.value ?? 0)}
                            min={0}
                            max={maxQuantity || undefined}
                            showButtons
                            disabled={!selectedMaterial}
                        />
                        {selectedMaterial && (
                            <small className="text-600">
                                {t('pages.maintenance_orders.available')}: {maxQuantity}
                            </small>
                        )}
                    </div>

                    <div className="flex flex-column gap-2 flex-1">
                        <label htmlFor="unit_cost" className="font-medium text-900">
                            {t('pages.maintenance_orders.unit_cost')}
                        </label>
                        <InputNumber
                            inputId="unit_cost"
                            value={unitCost}
                            mode="currency"
                            currency="EUR"
                            locale="es-ES"
                            disabled
                        />
                    </div>
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="date" className="font-medium text-900">
                        {t('pages.maintenance_orders.allocation_date')}
                    </label>
                    <Calendar
                        id="date"
                        value={date}
                        onChange={(e) => setDate((e.value as Date) ?? null)}
                        showTime
                        hourFormat="24"
                        showIcon
                        dateFormat="dd/mm/yy"
                    />
                </div>

                <div className="flex flex-column gap-2">
                    <label htmlFor="comments" className="font-medium text-900">
                        {t('pages.maintenance_orders.comments')}
                    </label>
                    <InputTextarea
                        id="comments"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        rows={3}
                        autoResize
                    />
                </div>

                {selectedMaterial && (
                    <div className="text-700 text-sm">
                        {t('pages.maintenance_orders.total_cost')}:{' '}
                        <span className="text-900 font-bold">
                            {currency.format(totalCost)}
                        </span>
                    </div>
                )}

                {error && <div className="text-red-500 text-sm">{error}</div>}
            </div>
        </Dialog>
    );
}

export default AssignMaterialModal;